import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems, marketplaceCurrency) => {
  const amount = lineItems.reduce((total, item) => {
    return total.plus(item.lineTotal.amount);
  }, new Decimal(0));
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  return new Money(amount, currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
};

/**
 * Returns non-commission, reversal line items
 */
const nonCommissionReversalLineItems = lineItems => {
  return lineItems.filter(item => !isCommission(item) && item.reversal);
};

const RESOLVE_PARTIAL_REFUND = 'transition/resolve-partial-refund';
const CANCEL_PARTIAL_REFUND = 'transition/cancel-partial-refund';

/**
 * For partial refund we use metadata.refundResult so customer and provider
 * see the correct refund amount (Flex reversal line items may reflect full refund).
 */
const getRefundFromMetadata = (transaction, marketplaceCurrency, isProvider) => {
  const metadata = transaction?.attributes?.metadata || {};
  const lastTransition = transaction?.attributes?.lastTransition;
  const isPartialRefund = [RESOLVE_PARTIAL_REFUND, CANCEL_PARTIAL_REFUND].includes(lastTransition);
  if (!isPartialRefund) return null;
  const { refundResult, originalPayoutTotal } = metadata;
  if (refundResult?.amount == null) return null;
  const currency = transaction?.attributes?.payinTotal?.currency || marketplaceCurrency;
  // Negative amount so it displays with minus (e.g. -$72.00), same as reversal line items
  return new Money(
    -Math.max(
      refundResult.amount >= originalPayoutTotal && isProvider
        ? originalPayoutTotal
        : refundResult.amount,
      0
    ),
    currency
  );
};

/**
 * A component that renders the refund as a line item.
 * For partial refund (resolve-partial-refund, cancel-partial-refund) uses
 * metadata.refundResult so the correct amount is shown for both customer and provider.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {propTypes.transaction} props.transaction - The transaction (for metadata.refundResult)
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemRefundMaybe = props => {
  const { lineItems, intl, marketplaceCurrency, isProvider, transaction } = props;

  const refundFromMetadata = transaction
    ? getRefundFromMetadata(transaction, marketplaceCurrency, isProvider)
    : null;

  if (refundFromMetadata) {
    const formattedRefund = formatMoney(intl, refundFromMetadata);
    return (
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.refund" />
        </span>
        <span className={css.itemValue}>{formattedRefund}</span>
      </div>
    );
  }

  // Fallback: sum non-commission reversal line items (full refund or no refundResult yet)
  const refundLineItems = nonCommissionReversalLineItems(lineItems);
  const refund = lineItemsTotal(refundLineItems, marketplaceCurrency);
  const formattedRefund = refundLineItems.length > 0 ? formatMoney(intl, refund) : null;

  return formattedRefund ? (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="OrderBreakdown.refund" />
      </span>
      <span className={css.itemValue}>{formattedRefund}</span>
    </div>
  ) : null;
};

export default LineItemRefundMaybe;
